import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ProjectsApiActions, ProjectsUiActions } from '../actions';
import { catchError, filter, map, mergeMap, of } from 'rxjs';
import { ProjectService } from '../../services/projects.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { IProjectPermissionGroups, extractSeqeraErrorMessage } from '../../models/Project';
import { ProjectFacade } from '../../facades/project.facade';
import { LabFacade } from '../../../labs/facade/lab.facade';
import { LabsUiActions } from '../../../labs/state/actions';

@Injectable()
export class ProjectsEffects {
  private actions$ = inject(Actions);
  private projectService = inject(ProjectService);
  private toastService = inject(ToastService);
  private projectFacade = inject(ProjectFacade);
  private labFacade = inject(LabFacade);
  projectSelected = this.projectFacade.projectSelectedSignal()?.id;

  getProjectList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.GetProjects),
      mergeMap(() =>
        this.projectService.getProjects().pipe(
          map((projects) => ProjectsApiActions.getProjectListSuccess({ payload: projects })),
          catchError((error) => {
            this.toastService.show(`Error loading projects: ${error.message || 'Unexpected error'}`, 'error');
            return of(ProjectsApiActions.getProjectListError({ errors: error }));
          })
        )
      )
    )
  );
  postProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.PostProject),
      mergeMap(({ project }) =>
        this.projectService.postProject(project).pipe(
          map((createdProject) => {
            this.toastService.show('Project created successfully', 'success');
            return ProjectsApiActions.postProjectSuccess({ payload: createdProject });
          }),
          catchError((error) => {
            this.toastService.show(`Error creating project: ${error.message || 'Unexpected error'}`, 'error');
            return of(ProjectsApiActions.postProjectError({ errors: error }));
          })
        )
      )
    )
  );

  getProjectDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.GetProjectDetails),
      mergeMap(({ projectId }) =>
        this.projectService.getProjectDetail(projectId).pipe(
          map((project) => ProjectsApiActions.getProjectDetailsSuccess({ payload: project })),
          catchError((error) => {
            this.toastService.show(`Error loading project detail: ${error.message || 'Unexpected error'}`, 'error');
            return of(ProjectsApiActions.getProjectDetailsError({ errors: error }));
          })
        )
      )
    )
  );

  getProjectPermissionGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.GetProjectPermissionGroups),
      mergeMap(() =>
        this.projectService.getProjectPermissions().pipe(
          map((permissions) =>
            ProjectsApiActions.getProjectPermissionGroupsSuccess({ payload: permissions as IProjectPermissionGroups[] })
          ),
          catchError((error) => {
            this.toastService.show(`Error loading permissions: ${error.message || 'Unexpected error'}`, 'error');
            return of(ProjectsApiActions.getProjectPermissionGroupsError({ errors: error }));
          })
        )
      )
    )
  );
  postProjectUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.PostProjectUser),
      mergeMap(({ projectUser }) =>
        this.projectService.postAssociateProjectUser(projectUser).pipe(
          map(() => {
            this.toastService.show('User added to project successfully', 'success');
            return ProjectsApiActions.postProjectUserSuccess({ payload: projectUser });
          }),
          catchError((error) => {
            const errorMsg = error.error.non_field_errors[0];

            this.toastService.show(`Error adding user to project: ${errorMsg}`, 'error');

            return of(ProjectsApiActions.postProjectUserError({ errors: error }));
          })
        )
      )
    )
  );

  getProjectUsersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.GetProjectUserList),
      mergeMap(() =>
        this.projectService.getProjectUsers().pipe(
          map((users) => ProjectsApiActions.getProjectUsersSuccess({ payload: users })),
          catchError((error) => {
            const errorMsg = error.error.non_field_errors[0];

            this.toastService.show(` ${errorMsg}`, 'error');
            return of(ProjectsApiActions.getProjectUsersError({ errors: error }));
          })
        )
      )
    )
  );

  patchProjectUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.PatchProjectUser),
      mergeMap(({ projectUser }) =>
        this.projectService.patchProjectUser(projectUser).pipe(
          map(() => {
            this.toastService.show('User updated successfully', 'success');
            return ProjectsApiActions.patchProjectUserSuccess({ payload: projectUser });
          }),
          catchError((error) => {
            const errorMsg = error.error.non_field_errors[0];

            this.toastService.show(` ${errorMsg}`, 'error');
            return of(ProjectsApiActions.patchProjectUserError({ errors: error }));
          })
        )
      )
    )
  );

  postProjectSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsApiActions.postProjectUserSuccess),
      mergeMap(() => {
        const selectedProject = this.projectFacade.projectSelectedSignal();
        const projectId = selectedProject?.id ?? 0;
        // Prefer the synchronously-updated selectedLab slice; fall back to
        // the lab detail slice. Never coerce a missing id to 0 — that
        // produces /api/labs/0/members/ which 404s.
        const labId = this.labFacade.selectedLabSignal()?.id ?? this.labFacade.getLabDetailSignal()?.id;

        const actions: Action[] = [];
        if (projectId > 0) {
          actions.push(ProjectsUiActions.GetProjectDetails({ projectId }));
        }
        if (labId && labId > 0) {
          actions.push(LabsUiActions.GetLabProjectUsersList({ labId }));
        }
        return of(...actions);
      })
    )
  );

  patchProjectUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsApiActions.patchProjectUserSuccess),
      mergeMap(() => {
        const project = this.projectFacade.selectedProjectUserSignal()?.project;
        const projectId = project?.id ?? 0;
        const labId = this.labFacade.selectedLabSignal()?.id ?? this.labFacade.getLabDetailSignal()?.id;

        const actions: Action[] = [];
        if (projectId > 0) {
          actions.push(ProjectsUiActions.GetProjectDetails({ projectId }));
        }
        if (labId && labId > 0) {
          actions.push(LabsUiActions.GetLabProjectUsersList({ labId }));
        }
        return of(...actions);
      })
    )
  );

  deleteProjectUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.DeleteProjectUser),
      mergeMap(({ projectUserId }) =>
        this.projectService.removeProjectUser(projectUserId).pipe(
          map(() => {
            this.toastService.show('User removed from project successfully', 'success');
            return ProjectsApiActions.deleteProjectUserSuccess({ payload: projectUserId });
          }),
          catchError((error) => {
            const errorMsg = error.error.non_field_errors[0];

            this.toastService.show(` ${errorMsg}`, 'error');
            return of(ProjectsApiActions.deleteProjectUserError({ errors: error }));
          })
        )
      )
    )
  );

  deleteProjectUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsApiActions.deleteProjectUserSuccess),
      filter(() => this.projectSelected !== null),
      map(() => {
        const selectedProject = this.projectFacade.selectedProjectUserSignal()?.project;
        return ProjectsUiActions.GetProjectDetails({
          projectId: selectedProject?.id ?? 0,
        });
      })
    )
  );

  retrySeqeraMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsUiActions.RetrySeqeraMember),
      mergeMap(({ projectId, failureId }) =>
        this.projectService.retrySeqeraMember(projectId, failureId).pipe(
          map((response) => {
            this.toastService.show(response.detail || 'User added to Seqera workspace', 'success');
            return ProjectsApiActions.retrySeqeraMemberSuccess({ projectId, failureId });
          }),
          catchError((error) => {
            const errorMsg = extractSeqeraErrorMessage(error?.error?.detail);
            this.toastService.show(errorMsg, 'error');
            return of(ProjectsApiActions.retrySeqeraMemberError({ errors: error, failureId }));
          })
        )
      )
    )
  );

}
